import 'react-sliding-side-panel/lib/index.css'

import { Hidden } from '@mui/material'
import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { Dispatch, memo, useRef, useState } from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SlidingPanel from 'react-sliding-side-panel'
import { PremiumModal } from 'src/components/global/PremiumModal'
import { Slidingbar } from 'src/components/global/Slidingbar'
import { AccountMenu } from 'src/components/Header/AccountMenu'
import Logo from 'src/components/Header/Logo'
// import { TopicsMenu } from 'src/components/Header/TopicsMenu'
// import * as enVariables from 'src/config/config'
import { useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { AppState } from 'src/store/types'
import { logout } from 'src/utils/authLogout'

import { VendorState } from '../EditListing/VendorStateModal'
//import { data } from 'tailwindcss/defaultTheme'
import { EventsMenu } from './EventsMenu'
import styles from './Header.module.scss'
import { IndustryAnalysisMenu } from './IndustryAnalysisMenu'
import { InsightsMenu } from './InsightsMenu'
import { SolutionsMenu } from './SolutionsMenu'
import { TopicsMenu } from './TopicsMenu'

export interface MenuItem {
  title: string
  subMenuContained: boolean
}

interface HeaderProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topicData?: any
}

export const Header: React.FC<HeaderProps> = memo(({ topicData = null }) => {
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const router = useRouter()

  // Check if topics data exists in localStorage - convert to boolean
  const hasTopicsInStorage = typeof window !== 'undefined' && !!localStorage.getItem('topics')

  // Only call the query if there's no data in localStorage and no topicData prop
  const { data: queryData } = useGetTopicsForHeaderQuery({
    fetchPolicy: 'cache-first',
    skip: hasTopicsInStorage || topicData !== null,
  })

  const [topicsData, setTopicsData] = useState([])
  const [topicCurrentIdx, setTopicCurrentIdx] = useState(0)
  const [isPaneOpen, setIsPaneOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPanelClosing, setIsPanelClosing] = useState(false) // Add this state
  /* const onClosePanel = () => {
    setIsPaneOpen(false)
  } */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()

  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [showVendorState, setShowVendorState] = useState(false)

  // Note: Token change handling (login/logout) is now centralized in _app.tsx
  // This component will re-render automatically when Redux state updates via _app.tsx handlers

  useEffect(() => {
    let topics = []

    if (topicData !== null) {
      topics = topicData
      localStorage.setItem('topics', JSON.stringify(topicData))
    } else if (topicData === null) {
      const topicsDataLocal = localStorage.getItem('topics')
      const data = topicsDataLocal ? JSON.parse(topicsDataLocal) : []

      if (data && data.length > 0) {
        topics = data
      } else {
        topics = queryData?.topics || []
        if (topics.length > 0) {
          localStorage.setItem('topics', JSON.stringify(topics))
        }
      }
    }

    setTopicsData(topics)
    setIsLoading(true)

    /* const id = setInterval(() => {
      setIsLoading(true)
      clearInterval(id)
    }, 1000) */
  }, [queryData, topicData])

  const MENU_OPTIONS: MenuItem[] = [
    { title: 'LTH Solutions', subMenuContained: true },
    { title: 'Insights', subMenuContained: true },
    { title: 'Topics', subMenuContained: true },
    { title: 'Events', subMenuContained: true },
    { title: 'Industry Analysis', subMenuContained: true },
    /* { title: 'Legaltech Jobs', subMenuContained: false }, */
    { title: 'About Us', subMenuContained: false },
    { title: 'Sign In', subMenuContained: false },
    { title: 'Sign Up', subMenuContained: false },
  ]

  const MENU_OPTIONS_SIGNIN: MenuItem[] = [
    { title: 'LTH Solutions', subMenuContained: true },
    { title: 'Insights', subMenuContained: true },
    { title: 'Topics', subMenuContained: true },
    { title: 'Events', subMenuContained: true },
    { title: 'Industry Analysis', subMenuContained: true },
    /* { title: 'Legaltech Jobs', subMenuContained: false }, */
    { title: 'About Us', subMenuContained: false },
    { title: 'My Account', subMenuContained: true },
  ]

  const [activeMenu, setActiveMenu] = useState<string | null>(null)

  /* const handleSelectMenu = (title: string) => {
    if (title === 'Events') {
      setIsPaneOpen(false)
      router.push({ pathname: '/search', query: { type: 'upcoming' } })
    } else if (title === 'About Us') {
      setIsPaneOpen(false)
      router.push({ pathname: '/about' })
    }
  }
 */

  const menuRef = useRef<HTMLDivElement | null>(null) // Ref for detecting outside clicks

  // Handle clicking outside the open menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null) // Close the active menu when clicking outside
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleMenuToggle = (menuName: string) => {
    // If the same menu is clicked, close it; otherwise, switch to the new one
    setActiveMenu((prevMenu) => (prevMenu === menuName ? null : menuName))
  }

  const onClosePanel = () => {
    setIsPanelClosing(true) // Set closing state first
    setIsPaneOpen(false)
    setActiveMenu(null)
    // Reset closing state after a short delay
    setTimeout(() => setIsPanelClosing(false), 100)
  }

  const handleSelectMenu = (menuName: string) => {
    setIsPaneOpen(false)
    setActiveMenu(menuName)
  }

  const signOut = async () => {
    // Use centralized logout function
    await logout('User initiated logout', dispatch)
    // Navigate to home page
    router.push('/')
  }

  const handlePremiumModalClose = () => {
    setShowPremiumModal(false)
  }

  return (
    <div className={cn('w-full flex flex-row justify-center px-5 sm:px-0 bg-navy h-[3.1875rem]')}>
      {isLoading && (
        <div className="!max-w-[1100px] w-full h-full flex flex-row items-center justify-between gap-3">
          <Link
            className="mr-0 flex-shrink-0 md:mr-[40px]"
            href="/"
            title="Legaltech Hub"
            passHref={true}
            prefetch={false}
            onClick={(e) => {
              if (router.pathname === '/') {
                e.preventDefault()
                window.location.reload()
              }
            }}
          >
            <Logo isPremium={authInfo.isPremium} isVendor={authInfo.isVendor} className="w-[12.5rem] h-[3.1875rem]" />
          </Link>
          <Hidden mdUp>
            <div className="ml-auto flex h-full flex-row items-center justify-end">
              <IonIcon
                className="items-center justify-center text-[30px]  !text-white"
                name="menu"
                onClick={() => {
                  setIsPaneOpen(true)
                }}
              />
              <div className="topHeaderMenu" style={{ backgroundColor: '#011d58' }}>
                <SlidingPanel type={'right'} isOpen={isPaneOpen} size={90}>
                  <Slidingbar
                    topics={topicsData}
                    options={!authInfo.idToken ? MENU_OPTIONS : MENU_OPTIONS_SIGNIN}
                    onSelectMenu={handleSelectMenu}
                    onClosePanel={onClosePanel}
                    onSignout={signOut}
                    onShowPremiumModal={() => setShowPremiumModal(true)} // Pass callback
                    isPanelClosing={isPanelClosing} // Pass the closing state
                    setShowVendorState={(item: boolean) => setShowVendorState(item)}
                  />
                </SlidingPanel>
              </div>
            </div>
          </Hidden>
          <Hidden mdDown>
            <div ref={menuRef} className="topHeaderMenu flex flex-row items-center h-full gap-3">
              <div onClick={() => handleMenuToggle('solutions')}>
                <SolutionsMenu isOpen={activeMenu === 'solutions'} />
              </div>
              <div onClick={() => handleMenuToggle('insights')}>
                <InsightsMenu isOpen={activeMenu === 'insights'} />
              </div>
              <div onClick={() => handleMenuToggle('topics')}>
                <TopicsMenu
                  topicsData={topicsData}
                  topicCurrentIdx={topicCurrentIdx}
                  handleSetTopicCurrentIdx={setTopicCurrentIdx}
                  isOpen={activeMenu === 'topics'}
                />
              </div>
              <div onClick={() => handleMenuToggle('events')}>
                <EventsMenu isOpen={activeMenu === 'events'} />
              </div>
              <div onClick={() => handleMenuToggle('industryAnalysis')}>
                <IndustryAnalysisMenu isOpen={activeMenu === 'industryAnalysis'} onClosePanel={onClosePanel} />
              </div>
              <Link href="/about" prefetch={false} className={cn(styles.menuItem)}>
                About Us
              </Link>
              <div style={{ borderLeft: '1px solid #fff', height: '30px', width: '1px' }}></div>
              {/* Use Redux state (authInfo.idToken) as source of truth - prevents split-brain UI where localStorage has token but Redux has no profile */}
              {!authInfo.idToken ? (
                <div className="flex flex-row items-center relative gap-3">
                  <Link
                    prefetch={false}
                    href="/signin"
                    className={cn(styles.menuItem, 'relative inline-block h-full text-nowrap cursor-pointer text-lef')}
                  >
                    Sign In
                  </Link>
                  <Link
                    prefetch={false}
                    href="/registration"
                    className="flex flex-row items-center h-[40px] text-white font-bold font-secondary text-[14px] bold text-center rounded-[22px] bg-navybright px-[12px]"
                  >
                    Sign Up
                  </Link>
                </div>
              ) : (
                <div className="flex flex-row items-center relative" onClick={() => handleMenuToggle('account')}>
                  <p className={cn(styles.accountName)}>{authInfo.name?.slice(0, 1).toUpperCase()}</p>
                  <AccountMenu
                    isOpen={activeMenu === 'account'}
                    handleSignout={signOut}
                    isPremium={authInfo.isPremium}
                    isVendor={authInfo.isVendor}
                  />
                </div>
              )}
            </div>
          </Hidden>
        </div>
      )}

      {/* Render PremiumModal here so it persists */}
      <PremiumModal origin="/graveyard-consolidations/" isOpen={showPremiumModal} onClose={handlePremiumModalClose} />
      {showVendorState && (
        <VendorState
          isOpen={showVendorState}
          onClose={() => {
            setShowVendorState(false)
          }}
          vendorName={authInfo.vendor || ''}
        />
      )}
    </div>
  )
})
